import React from 'react';
import { Button } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { Box, useFluentTokens } from '../../../theme/fluent';
import { useInactivity } from '../../../providers';
import { useNavigate } from 'react-router-dom';
import { PAGE_PATHS } from '../../../features/app-config/constants/appConstants';
import { analyticsService } from '../../../services/analyticsService';

export const InactivityScreen: React.FC = () => {
  const { resetActivity } = useInactivity();
  const { tokens, modeTokens } = useFluentTokens();
  const navigate = useNavigate();

  const handleResume = async () => {
    analyticsService.trackEvent('session_resumed');
    await resetActivity();
  };

  const handleClose = async () => {
    analyticsService.trackEvent('session_dismissed');
    await resetActivity();
    navigate(PAGE_PATHS.DASHBOARD);
  };

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        zIndex: 2147483647,
        padding: tokens.spacingHorizontalL,
      }}
    >
      <Box
        style={{
          backgroundColor: modeTokens.colorNeutralBackground1,
          borderRadius: tokens.borderRadiusXLarge,
          padding: tokens.spacingHorizontalXXL,
          maxWidth: '300px',
          width: '100%',
          boxShadow: tokens.shadow64,
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.spacingVerticalS,
        }}
      >
        {/* Header with close button */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{
            gap: tokens.spacingHorizontalS,
          }}
        >
          <Box
            as="h2"
            style={{
              fontSize: tokens.fontSizeHero500,
              lineHeight: tokens.lineHeightHero500,
              fontWeight: tokens.fontWeightSemibold,
              color: modeTokens.colorNeutralForeground1,
              margin: 0,
            }}
          >
            Session expired
          </Box>
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            onClick={handleClose}
            style={{
              minWidth: '28px',
              padding: tokens.spacingVerticalSNudge,
            }}
          />
        </Box>

        {/* Body */}
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacingVerticalSNudge,
          }}
        >
          <Box
            as="p"
            style={{
              fontSize: tokens.fontSizeBase200,
              lineHeight: tokens.lineHeightBase200,
              fontWeight: tokens.fontWeightRegular,
              color: modeTokens.colorNeutralForeground1,
              margin: 0,
            }}
          >
            Your session expired while you were away.
            <br />
            Want to resume the session and pick up where you left off?
          </Box>
        </Box>

        {/* Footer with Resume button */}
        <Box
          style={{
            paddingTop: tokens.spacingHorizontalXS,
          }}
        >
          <Button
            appearance="primary"
            onClick={handleResume}
            style={{
              borderRadius: tokens.borderRadiusXXLarge,
              fontWeight: tokens.fontWeightSemibold,
              fontSize: tokens.fontSizeBase300,
              lineHeight: tokens.lineHeightBase300,
              width: '100%',
              minWidth: '96px',
              paddingTop: tokens.spacingVerticalSNudge,
              paddingBottom: tokens.spacingVerticalSNudge,
            }}
          >
            Resume session
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
